import React, { memo, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { TimesheetFilters } from 'Components/TimeTracking';
import { useTimeTrackingFunctions } from 'Context/TimeTracking';

import { listEmployeesForTimesheets, listProjectsAndAddressesForTimesheets } from 'Containers/TimeTracking/actions';

import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { formatDate } from 'Utils/helpers';

const TimesheetFiltersContainer = () => {
  const filterContainerRef = useRef<HTMLDivElement>(null);

  const {
    employeeFilters,
    projectNumberFilters,
    savedEmployeeFilters,
    savedProjectNumFilters,
    setSavedEmployeeFilters,
    setSavedProjectNumFilters,
    setSavedDateFilter,
    setIsDateFilterApplied,
    dateFilterStart,
    dateFilterEnd,
    setDateFilterStart,
    setDateFilterEnd,
  }: any = useTimeTrackingFunctions();

  const dispatch = useDispatch();
  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);

  useEffect(() => {
    if (firstCompanyId) {
      dispatch(listEmployeesForTimesheets(firstCompanyId));
      dispatch(listProjectsAndAddressesForTimesheets(firstCompanyId));
    }
  }, [firstCompanyId]);

  const onEmployeeChange = useCallback(
    (e: any) => {
      const val = e.target.value;
      setSavedEmployeeFilters(val ? [Number(val)] : []);
    },
    [setSavedEmployeeFilters]
  );

  const onProjectChange = useCallback(
    (e: any) => {
      const val = e.target.value;
      setSavedProjectNumFilters(val ? [Number(val)] : []);
    },
    [setSavedProjectNumFilters]
  );

  const onChangeDate = useCallback(
    (dates: any) => {
      const [start, end] = dates;
      setDateFilterStart(start);
      setDateFilterEnd(end);
      if (start && end) {
        setSavedDateFilter({
          startDate: formatDate(start, 'yyyy-MM-dd'),
          endDate: formatDate(end, 'yyyy-MM-dd'),
        });
        setIsDateFilterApplied(true);
      }
    },
    [setSavedDateFilter, setIsDateFilterApplied, setDateFilterStart, setDateFilterEnd]
  );

  const onClearDate = useCallback(() => {
    setDateFilterStart(null);
    setDateFilterEnd(null);
    setSavedDateFilter(null);
    setIsDateFilterApplied(false);
  }, [setSavedDateFilter, setIsDateFilterApplied, setDateFilterStart, setDateFilterEnd]);

  return (
    <TimesheetFilters
      containerRef={filterContainerRef}
      startDate={dateFilterStart}
      endDate={dateFilterEnd}
      employeeFilters={employeeFilters}
      selectedEmployeeId={savedEmployeeFilters.length > 0 ? savedEmployeeFilters[0].toString() : ''}
      projectNumberFilters={projectNumberFilters}
      selectedProjectId={savedProjectNumFilters.length > 0 ? savedProjectNumFilters[0].toString() : ''}
      onEmployeeChange={onEmployeeChange}
      onProjectChange={onProjectChange}
      onChangeDate={onChangeDate}
      onClearDate={onClearDate}
    />
  );
};

const TimesheetFiltersContainerMemo = memo(TimesheetFiltersContainer, areEqual);

export { TimesheetFiltersContainerMemo as TimesheetFilters };
